'use client'

import { IconStar } from '@/app/components/atoms/Icons'
import {
  RATING_PROMPT_EMPTY,
  RATING_PROMPT_SUMMARY,
  REVIEWS_MODULE_NAME,
  REVIEWS_WRITE_LINK_TEXT,
} from '@/app/components/sections/performance/PerformanceReviews/constants'
import { useOptionalPageAnalytics } from '@/app/context/analytics'
import { NO_APLICA } from '@/app/lib/analytics/constants'
import { cx } from '@/app/lib/utils'

import styles from './BazaarvoiceWriteReview.module.scss'
import type { BazaarvoiceWriteReviewProps } from './BazaarvoiceWriteReview.types'

const STAR_COUNT = 5
const CTA_LABEL = 'Déjanos tu opinión'

/**
 * `BazaarvoiceWriteReview` — punto de entrada de valoración de la rama
 * Bazaarvoice, fiel al proyecto de diseño (Figma 5900:37443 mobile /
 * 5900:33900 desktop sin reseñas, 5900:34468 con reseñas): aviso, cinco
 * estrellas de 36px en acento y el enlace "Déjanos tu opinión" subrayado.
 *
 * Es el mismo bloque que pinta el sistema propio (`OwnRatingForm`), pero aquí
 * no hay voto propio que emitir: el envío lo gestiona íntegramente Bazaarvoice
 * en su modal "Mi reseña" (puntuación, título, texto, alias, correo y
 * consentimiento). Por eso las estrellas NO son un control de selección — son
 * la superficie visible del mismo disparador que el enlace — y se exponen como
 * un único botón, no cinco radios: cinco controles que abren exactamente el
 * mismo diálogo serían ruido para un lector de pantalla.
 *
 * `window.$BV` solo existe una vez que `bv.js` (montado por
 * `BazaarvoiceScriptLoader`) ha terminado de cargar. Si falla o lo bloquea un
 * extensión, el clic no hace nada en vez de romper: preferible a esconder el
 * bloque, porque su carga es asíncrona y no se sabe si está "aún cargando" o
 * "no va a cargar".
 */
export function BazaarvoiceWriteReview({
  productId,
  hasReviews,
  variant = 'default',
}: BazaarvoiceWriteReviewProps) {
  const analytics = useOptionalPageAnalytics()

  function openSubmission() {
    // `Opiniones.csv` → "CTA Escribe una opinión" (12 parámetros fijos).
    analytics?.trackClick({
      click_type: 'content_link',
      click_subtype: NO_APLICA,
      link_text: REVIEWS_WRITE_LINK_TEXT,
      link_url: NO_APLICA,
      link_domain: NO_APLICA,
      module_name: REVIEWS_MODULE_NAME,
      module_type: NO_APLICA,
      nav_level: NO_APLICA,
      nav_path: NO_APLICA,
      action_detail: NO_APLICA,
      action_value: NO_APLICA,
      action_context: NO_APLICA,
    })
    window.$BV?.ui('rr', 'submit_review', { productId })
  }

  if (variant === 'channel' && hasReviews) {
    return (
      <button
        type="button"
        className={styles.linkOnly}
        onClick={openSubmission}
        aria-haspopup="dialog"
      >
        {CTA_LABEL}
      </button>
    )
  }

  return (
    <div className={cx(styles.root, !hasReviews && styles.empty)}>
      <p className={styles.prompt}>{hasReviews ? RATING_PROMPT_SUMMARY : RATING_PROMPT_EMPTY}</p>

      <button
        type="button"
        className={styles.stars}
        onClick={openSubmission}
        aria-haspopup="dialog"
        aria-label={CTA_LABEL}
      >
        {Array.from({ length: STAR_COUNT }, (_, index) => (
          <IconStar key={index} size={36} />
        ))}
      </button>

      <button type="button" className={styles.cta} onClick={openSubmission} aria-haspopup="dialog">
        {CTA_LABEL}
      </button>
    </div>
  )
}
